import { Alert, Box, Chip, CircularProgress, LinearProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack, Typography } from '@mui/material';
import { tr } from '../lib/i18n';
import { Button } from './button';
import type { ReactNode } from 'react';
import { ApiError } from '../api/client';

export function Loading({ label = tr('جارٍ التحميل…', 'Loading…'), size = 40 }: { label?: string; size?: number | string }) {
  return (
    <Stack spacing={2} direction="row" sx={{ alignItems: 'center', justifyContent: 'center', py: size === '30px' || size === 30 ? 2 : 6 }}>
      <CircularProgress size={size} aria-label={label} />
    </Stack>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const msg = error instanceof ApiError ? error.message : error instanceof Error ? error.message : tr('تعذر تحميل البيانات', 'Unable to load data');
  return (
    <Alert severity="error" action={onRetry && <Button color="inherit" size="small" onClick={onRetry}>{tr('إعادة المحاولة', 'Retry')}</Button>}>
      {msg}
    </Alert>
  );
}

export function Empty({ text, icon, action }: { text: string; icon?: ReactNode; action?: ReactNode }) {
  return (
    <Stack alignItems="center" spacing={1.25} sx={{ py: 5, px: 2, textAlign: 'center', color: 'text.secondary' }}>
      {icon && (
        <Box sx={{ width: 56, height: 56, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: (t) => t.palette.m3.surfaceContainerHigh, '& svg': { fontSize: 28 } }}>
          {icon}
        </Box>
      )}
      <Typography variant="body2">{text}</Typography>
      {action}
    </Stack>
  );
}

/** Full-screen first load (session check): the mark + a thin progress bar, like Google apps. */
export function Splash() {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={3} sx={{ height: '100%', bgcolor: 'background.default' }}>
      <Box component="img" src="/favicon-light.png" alt="" sx={{ width: 56, height: 56, borderRadius: '14px' }} />
      <LinearProgress sx={{ width: 160, height: 4 }} aria-label={tr('جارٍ التحميل…', 'Loading…')} />
    </Stack>
  );
}

export function ActiveChip({ active }: { active: boolean }) {
  return <Chip size="small" label={active ? tr('نشط', 'Active') : tr('موقوف', 'Disabled')} color={active ? 'success' : 'default'} variant={active ? 'filled' : 'outlined'} />;
}

export function ConfirmDialog(p: { open: boolean; title: string; text: ReactNode; confirmLabel?: string; danger?: boolean; busy?: boolean; disabled?: boolean; onConfirm: () => void; onClose: () => void; children?: ReactNode }) {
  return (
    <Dialog open={p.open} onClose={p.onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{p.title}</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">{p.text}</DialogContentText>
        {p.children}
      </DialogContent>
      <DialogActions>
        <Button onClick={p.onClose}>{tr('إلغاء', 'Cancel')}</Button>
        <Button variant="contained" color={p.danger ? 'error' : 'primary'} onClick={p.onConfirm} loading={p.busy} disabled={p.disabled}>
          {p.confirmLabel ?? tr('تأكيد', 'Confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
